import { useParams, Link } from 'react-router-dom'
import { useConfig } from '../../hooks/useConfig'
import { DetailEntityPage } from '../DetailEntityPage/DetailEntityPage'
import { ConfigTagsTable } from '../../components/ConfigTagsTable/ConfigTagsTable'
import { Badge } from '../../components/Badge/Badge'
import { formatDate } from '../../helpers/formatDate'
import { deriveConfigScope } from '../../helpers/deriveConfigScope'
import { decodeConfigId } from '../../helpers/decodeConfigId'
import { buildConfigQuery } from '../../helpers/buildConfigQuery'
import { buildConfigDetailTitle } from '../../helpers/buildConfigDetailTitle'
import { buildConfigScopeDetailFields } from './helpers/buildConfigScopeDetailFields'
import type { DetailFieldDef } from '../../types'

export function ConfigDetailPage() {
  const { id } = useParams<{ id: string }>()
  const configFields = id ? decodeConfigId(id) : null
  const query = configFields ? buildConfigQuery(configFields) : ''
  const { configs, isLoading, error } = useConfig(query)

  const config = configFields
    ? (configs.find(
        (c) =>
          c.configKey === configFields.configKey &&
          c.tenantCode === configFields.tenantCode &&
          c.inventorySystemProfileCode ===
            configFields.inventorySystemProfileCode &&
          c.venueId === configFields.venueId
      ) ?? null)
    : null

  const scope = config ? deriveConfigScope(config) : null

  const fields: DetailFieldDef[] = config
    ? [
        {
          label: 'ID',
          value: <span className="font-monospace small">{config.id}</span>,
        },
        {
          label: 'Scope',
          value: scope ? <Badge color="blue">{scope}</Badge> : null,
        },
        ...(scope
          ? buildConfigScopeDetailFields(
              scope,
              config.tenantCode,
              config.inventorySystemProfileCode,
              config.venueId
            )
          : []),
        {
          label: 'Key',
          value: <span className="fw-medium">{config.configKey}</span>,
        },
        { label: 'Value', value: config.configValue },
        {
          label: 'Type',
          value: <Badge color="amber">{config.configType}</Badge>,
        },
        {
          label: 'Visible To Frontend',
          value: config.visibleToFrontend ? (
            <Badge color="red">Public</Badge>
          ) : (
            <Badge color="grey">Private</Badge>
          ),
        },
        { label: 'Tags', value: <ConfigTagsTable tags={config.tags} /> },
        { label: 'Created', value: formatDate(config.createdAt) },
        { label: 'Updated', value: formatDate(config.updatedAt) },
      ]
    : []

  return (
    <DetailEntityPage
      title={config ? buildConfigDetailTitle(config) : 'Config'}
      loadingLabel="config"
      fields={fields}
      isLoading={isLoading}
      error={error}
      actions={
        id && config ? (
          <>
            <Link
              to={`/configs/${id}/edit`}
              className="btn btn-primary btn-sm me-2"
            >
              Edit
            </Link>
            <Link
              to={`/configs/${id}/delete`}
              className="btn btn-danger btn-sm"
            >
              Delete
            </Link>
          </>
        ) : undefined
      }
    />
  )
}
